/**
 * Overlay Canvas Component
 *
 * Phone-frame stage for editing overlays with drag/resize/rotate.
 * Uses DOM + CSS transforms (no canvas library).
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { OverlayLayer } from './OverlayLayer';
import { useOverlayGesture } from './useOverlayGesture';
import { pctToPx } from './overlay-math';
import type { OverlayElement } from '../types';

interface OverlayCanvasProps {
  overlays: OverlayElement[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onUpdate: (id: string, transform: OverlayElement['transform']) => void;
  onDelete: (id: string) => void;
}

export function OverlayCanvas({
  overlays,
  selectedId,
  onSelect,
  onUpdate,
  onDelete,
}: OverlayCanvasProps) {
  const stageRef = useRef<HTMLDivElement>(null as HTMLDivElement | null);
  const [stageRect, setStageRect] = useState<DOMRect | null>(null);
  const [imageDimensions, setImageDimensions] = useState<
    Map<string, { width: number; height: number }>
  >(new Map());

  const {
    gesture,
    draftTransform,
    handlePointerDown: gesturePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
  } = useOverlayGesture({
    stageRef,
    onUpdate: onUpdate,
  });

  // ResizeObserver to track stage size
  useEffect(() => {
    if (!stageRef.current) return;

    const updateRect = () => {
      if (stageRef.current) {
        setStageRect(stageRef.current.getBoundingClientRect());
      }
    };

    updateRect();

    const observer = new ResizeObserver(updateRect);
    observer.observe(stageRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Global pointer move/up handlers for gestures
  useEffect(() => {
    if (!gesture) return;

    const handleGlobalMove = (e: PointerEvent) => {
      handlePointerMove(e);
    };
    const handleGlobalUp = (e: PointerEvent) => {
      handlePointerUp(e);
    };
    const handleGlobalCancel = (e: PointerEvent) => {
      handlePointerCancel(e);
    };

    window.addEventListener('pointermove', handleGlobalMove);
    window.addEventListener('pointerup', handleGlobalUp);
    window.addEventListener('pointercancel', handleGlobalCancel);

    return () => {
      window.removeEventListener('pointermove', handleGlobalMove);
      window.removeEventListener('pointerup', handleGlobalUp);
      window.removeEventListener('pointercancel', handleGlobalCancel);
    };
  }, [gesture, handlePointerMove, handlePointerUp, handlePointerCancel]);

  const getOverlayCenter = useCallback(
    (overlay: OverlayElement): { x: number; y: number } | undefined => {
      if (!stageRect) return undefined;
      const overlayX = stageRect.left + pctToPx(overlay.transform.xPct, stageRect.width);
      const overlayY = stageRect.top + pctToPx(overlay.transform.yPct, stageRect.height);
      return { x: overlayX, y: overlayY };
    },
    [stageRect]
  );

  const handleOverlayPointerDown = useCallback(
    (e: React.PointerEvent, overlayId: string, overlay: OverlayElement) => {
      e.stopPropagation();
      onSelect(overlayId);

      // Focus stage for keyboard events
      stageRef.current?.focus();

      const center = getOverlayCenter(overlay);
      if (!center) return;

      gesturePointerDown(e, overlayId, overlay, 'drag', center);
    },
    [onSelect, getOverlayCenter, gesturePointerDown]
  );

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent, overlayId: string, overlay: OverlayElement) => {
      e.stopPropagation();
      onSelect(overlayId);
      stageRef.current?.focus();

      const center = getOverlayCenter(overlay);
      if (!center) return;

      gesturePointerDown(e, overlayId, overlay, 'resize', center);
    },
    [onSelect, getOverlayCenter, gesturePointerDown]
  );

  const handleRotatePointerDown = useCallback(
    (e: React.PointerEvent, overlayId: string, overlay: OverlayElement) => {
      e.stopPropagation();
      onSelect(overlayId);
      stageRef.current?.focus();

      const center = getOverlayCenter(overlay);
      if (!center) return;

      gesturePointerDown(e, overlayId, overlay, 'rotate', center);
    },
    [onSelect, getOverlayCenter, gesturePointerDown]
  );

  const handleStagePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Click on empty stage deselects
      if (e.target === e.currentTarget) {
        onSelect(null);
      }
    },
    [onSelect]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!selectedId || !stageRect) return;

      const overlay = overlays.find((o) => o.id === selectedId);
      if (!overlay) return;

      const isShift = e.shiftKey;
      const delta = isShift ? 5 : 1; // Shift = 5%, normal = 1%

      const newTransform: OverlayElement['transform'] = { ...overlay.transform };

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          newTransform.xPct = Math.max(0, overlay.transform.xPct - delta);
          onUpdate(selectedId, newTransform);
          break;
        case 'ArrowRight':
          e.preventDefault();
          newTransform.xPct = Math.min(100, overlay.transform.xPct + delta);
          onUpdate(selectedId, newTransform);
          break;
        case 'ArrowUp':
          e.preventDefault();
          newTransform.yPct = Math.max(0, overlay.transform.yPct - delta);
          onUpdate(selectedId, newTransform);
          break;
        case 'ArrowDown':
          e.preventDefault();
          newTransform.yPct = Math.min(100, overlay.transform.yPct + delta);
          onUpdate(selectedId, newTransform);
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          onDelete(selectedId);
          break;
      }
    },
    [selectedId, overlays, stageRect, onUpdate, onDelete]
  );

  const handleImageLoad = useCallback(
    (overlayId: string, img: HTMLImageElement) => {
      if (img.naturalWidth && img.naturalHeight) {
        setImageDimensions((prev) => {
          const next = new Map(prev);
          next.set(overlayId, {
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
          return next;
        });
      }
    },
    []
  );

  // Clean up imageDimensions when overlays are removed
  useEffect(() => {
    const overlayIds = new Set(overlays.map((o) => o.id));
    setImageDimensions((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const key of next.keys()) {
        if (!overlayIds.has(key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [overlays]);

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">Canvas Preview</div>
      <div
        ref={stageRef}
        className="relative mx-auto bg-gray-100 dark:bg-gray-800 rounded-sm overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500"
        style={{
          aspectRatio: '9 / 19.5',
          maxWidth: '375px',
          touchAction: 'none',
        }}
        onPointerDown={handleStagePointerDown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="application"
        aria-label="Overlay canvas"
      >
        {overlays.map((overlay) => {
          const isSelected = selectedId === overlay.id;
          const transform = gesture?.overlayId === overlay.id && draftTransform
            ? draftTransform
            : overlay.transform;

          return (
            <OverlayLayer
              key={overlay.id}
              overlay={{ ...overlay, transform }}
              isSelected={isSelected}
              onPointerDown={(e) => handleOverlayPointerDown(e, overlay.id, overlay)}
              onResizePointerDown={
                isSelected
                  ? (e) => handleResizePointerDown(e, overlay.id, overlay)
                  : undefined
              }
              onRotatePointerDown={
                isSelected
                  ? (e) => handleRotatePointerDown(e, overlay.id, overlay)
                  : undefined
              }
              imageDimensions={
                overlay.type === 'image' ? imageDimensions.get(overlay.id) || null : null
              }
              onImageLoad={
                overlay.type === 'image'
                  ? (img) => handleImageLoad(overlay.id, img)
                  : undefined
              }
            />
          );
        })}
      </div>
      <div className="text-xs text-muted-foreground text-center">
        Click overlay to select • Drag to move • Use handles to resize/rotate • Arrow keys to nudge (Shift+Arrow = 5%)
      </div>
    </div>
  );
}
